import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, ShoppingCart, ArrowLeft, Maximize2, AlertTriangle } from "lucide-react";
import html2canvas from "html2canvas";
import api from "@/services/api";
import { useHybridCart } from "@/contexts/HybridCartContext";

// Componentes genéricos
import { PersonalizadorPortada } from "@/components/personalizable/PersonalizadorPortada";
import { VistaHojasInternasPorCategoria } from "@/components/personalizable/VistaHojasInternasPorCategoria";
import { PreviewCapas } from "@/components/personalizable/PreviewCapas";

// Tipos y datos
import {
  ProductoPersonalizable,
  PortadaConfig,
  PersonalizacionCompleta,
  Estampado,
} from "@/components/personalizable/types";
import { estampadosService } from "@/services/estampadosService";

export default function PersonalizarProducto() {
  const { productSlug } = useParams<{ productSlug: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { addToCart } = useHybridCart();
  const previewRef = useRef<HTMLDivElement>(null);

  // Estados
  const [loading, setLoading] = useState(true);
  const [producto, setProducto] = useState<ProductoPersonalizable | null>(null);
  const [tamanoSeleccionado, setTamanoSeleccionado] = useState<string>(""); // ID del tamaño seleccionado
  const [portadaConfig, setPortadaConfig] = useState<PortadaConfig>({
    tipo: "estampado",
  });
  const [nombrePersonalizado, setNombrePersonalizado] = useState("");
  const [agregandoAlCarrito, setAgregandoAlCarrito] = useState(false);
  const [estampadosDisponibles, setEstampadosDisponibles] = useState<
    Estampado[]
  >([]);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  // Cargar producto desde API
  useEffect(() => {
    const cargarProducto = async () => {
      try {
        setLoading(true);

        // Obtener producto completo por slug usando el endpoint individual
        const response = await api.get(`/products/${productSlug}`);

        if (!response.data.success || !response.data.data) {
          toast({
            title: "Producto no encontrado",
            description: "No se encontró el producto que buscas",
            variant: "destructive",
          });
          navigate("/products");
          return;
        }

        const productoEncontrado = response.data.data;

        // DEBUG: Ver estructura completa del producto
        console.log("🔍 [DEBUG] Producto cargado:", productoEncontrado);
        console.log("🔍 [DEBUG] configuracionPersonalizable:", productoEncontrado.configuracionPersonalizable);
        console.log("🔍 [DEBUG] personalizacion:", productoEncontrado.configuracionPersonalizable?.personalizacion);

        // Validar que sea personalizable
        if (!productoEncontrado.esPersonalizable) {
          toast({
            title: "Producto no personalizable",
            description: "Este producto no permite personalización",
            variant: "destructive",
          });
          navigate(`/products/${productoEncontrado._id}`);
          return;
        }

        setProducto(productoEncontrado);

        // Configurar tamaño inicial (seleccionar el primero por defecto)
        const opcionesTamano =
          productoEncontrado.configuracionPersonalizable.personalizacion
            .opcionesTamano;
        if (opcionesTamano && opcionesTamano.length > 0) {
          setTamanoSeleccionado(opcionesTamano[0].id);
        }

        // Cargar hojas decorativas desde la API según categoría
        const config =
          productoEncontrado.configuracionPersonalizable.personalizacion;

        console.log("🔍 [DEBUG] config.permiteEstampados:", config.permiteEstampados);
        if (config.permiteEstampados) {
          try {
            // Cargar estampados según la categoría configurada en el producto
            const categoriaProducto = config.categoriaEstampados || "general";
            console.log("🔍 [DEBUG] Cargando estampados para categoría:", categoriaProducto);
            const estampados =
              await estampadosService.getEstampadosPorCategoria(
                categoriaProducto
              );
            console.log("🔍 [DEBUG] Estampados cargados:", estampados);
            setEstampadosDisponibles(estampados);

            if (estampados.length > 0) {
              setPortadaConfig({
                tipo: "estampado",
                estampado: estampados[0].id,
              });
            } else {
              console.warn("⚠️ [DEBUG] No hay estampados disponibles para esta categoría");
            }
          } catch (error) {
            console.error("❌ [DEBUG] Error al cargar hojas decorativas:", error);
            toast({
              title: "Aviso",
              description: "No se pudieron cargar las hojas decorativas",
              variant: "default",
            });
          }
        } else if (config.permiteImagenPersonalizada) {
          setPortadaConfig({
            tipo: "imagen-personalizada",
          });
        }
      } catch (error: any) {
        console.error("Error al cargar producto:", error);
        toast({
          title: "Error",
          description:
            error.response?.data?.message || "No se pudo cargar el producto",
          variant: "destructive",
        });
        navigate("/products");
      } finally {
        setLoading(false);
      }
    };

    if (productSlug) {
      cargarProducto();
    }
  }, [productSlug, navigate, toast]);

  // Obtener precio según tamaño seleccionado (reactivo)
  // Precios PERSONALIZABLES (incluyen el cargo de personalización):
  // Mini: $20 (vs $15 estándar)
  // Grande: $30 (vs $25 estándar)
  const precioFinal = useMemo(() => {
    if (!producto) return 0;

    const opcionesTamano =
      producto.configuracionPersonalizable.personalizacion.opcionesTamano;
    if (opcionesTamano && opcionesTamano.length > 0 && tamanoSeleccionado) {
      const tamano = opcionesTamano.find((t) => t.id === tamanoSeleccionado);
      return tamano ? tamano.precio : producto.price;
    }

    return producto.price;
  }, [producto, tamanoSeleccionado]);

  // Obtener orientación para el preview (prioriza estampado seleccionado)
  const orientacionPreview = useMemo(() => {
    // 1. Si hay estampado seleccionado, usar su orientación
    if (portadaConfig.tipo === "estampado" && portadaConfig.estampado) {
      const estampado = estampadosDisponibles.find(
        (e) => e.id === portadaConfig.estampado
      );
      if (estampado?.orientacion) return estampado.orientacion;
    }

    // 2. Si hay imagen personalizada, usar su orientación detectada al subir
    if (portadaConfig.tipo === "imagen-personalizada" && portadaConfig.orientacion) {
      return portadaConfig.orientacion;
    }

    // 3. Fallback: orientación del tamaño seleccionado (o el primero disponible)
    if (!producto) return "vertical";
    const opcionesTamano =
      producto.configuracionPersonalizable.personalizacion.opcionesTamano;
    if (opcionesTamano && opcionesTamano.length > 0) {
      const tamano = tamanoSeleccionado
        ? opcionesTamano.find((t) => t.id === tamanoSeleccionado) ?? opcionesTamano[0]
        : opcionesTamano[0];
      if (tamano?.orientacion) return tamano.orientacion;
    }

    // 4. Último fallback: orientación del primer estampado disponible
    if (estampadosDisponibles.length > 0 && estampadosDisponibles[0].orientacion) {
      return estampadosDisponibles[0].orientacion;
    }

    return "vertical";
  }, [producto, tamanoSeleccionado, portadaConfig, estampadosDisponibles]);

  // Clase de aspecto dinámica según orientación
  const aspectRatioClass =
    orientacionPreview === "horizontal"
      ? "aspect-[4/3]" // Horizontal: 4 ancho × 3 alto
      : "aspect-[3/4]"; // Vertical: 3 ancho × 4 alto

  // Obtener URL de cubierta según configuración del producto y orientación
  const obtenerUrlCubierta = () => {
    const cubiertaConfig =
      producto?.configuracionPersonalizable.personalizacion.cubierta;

    // Determinar orientación actual
    const esHorizontal = orientacionPreview === "horizontal";

    if (!cubiertaConfig) {
      // Fallback para productos sin configuración de cubierta
      if (esHorizontal) {
        return "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/general/agenda-horizontal-base-con-argollas.png";
      }
      return "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/general/agenda-base-con-argollas.png";
    }

    // Seleccionar URL según orientación y preferencia de argollas
    if (esHorizontal) {
      return cubiertaConfig.tieneArgollas
        ? "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/general/agenda-horizontal-base-con-argollas.png"
        : "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/general/agenda-horizontal-base-sin-argollas.png";
    } else {
      return cubiertaConfig.tieneArgollas
        ? cubiertaConfig.urlConArgollas
        : cubiertaConfig.urlSinArgollas;
    }
  };

  // Validar que la configuración esté completa
  const validarConfiguracion = (): boolean => {
    // Validar tamaño si hay opciones disponibles
    const opcionesTamano =
      producto?.configuracionPersonalizable.personalizacion.opcionesTamano;
    if (opcionesTamano && opcionesTamano.length > 0 && !tamanoSeleccionado) {
      toast({
        title: "Falta seleccionar tamaño",
        description: "Por favor selecciona un tamaño para tu producto",
        variant: "destructive",
      });
      return false;
    }

    // Validar portada
    if (portadaConfig.tipo === "estampado" && !portadaConfig.estampado) {
      toast({
        title: "Falta seleccionar estampado",
        description: "Por favor selecciona un estampado para la portada",
        variant: "destructive",
      });
      return false;
    }

    if (
      portadaConfig.tipo === "imagen-personalizada" &&
      !portadaConfig.imagenPersonalizada
    ) {
      toast({
        title: "Falta subir imagen",
        description: "Por favor sube una imagen personalizada para la portada",
        variant: "destructive",
      });
      return false;
    }

    // Validar nombre si es requerido
    const campoNombre =
      producto?.configuracionPersonalizable.personalizacion.campoNombre;
    if (campoNombre?.activo && !nombrePersonalizado.trim()) {
      toast({
        title: "Falta nombre personalizado",
        description: `Por favor ingresa ${campoNombre.label.toLowerCase()}`,
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  // Agregar al carrito
  const handleAddToCart = async () => {
    if (!producto || !validarConfiguracion()) return;

    try {
      setAgregandoAlCarrito(true);

      // Capturar preview de la configuración
      let previewImage = "";
      if (previewRef.current) {
        try {
          const canvas = await html2canvas(previewRef.current, {
            backgroundColor: "#FFFFFF", // Fondo blanco para evitar transparencias negras
            scale: 2,
            useCORS: true,
            logging: false,
          });
          const rawPreview = canvas.toDataURL("image/png");

          // Comprimir la imagen del preview para reducir tamaño
          const { compressBase64Image } = await import(
            "@/utils/imageValidator"
          );
          previewImage = await compressBase64Image(rawPreview, 100); // 100KB target

          console.log(
            `📸 Preview de producto capturado y comprimido exitosamente`
          );
        } catch (error) {
          console.warn("No se pudo generar preview:", error);
        }
      }

      // Obtener información del tamaño seleccionado
      const opcionesTamano =
        producto.configuracionPersonalizable.personalizacion.opcionesTamano;
      const tamanoInfo = opcionesTamano?.find(
        (t) => t.id === tamanoSeleccionado
      );

      // Construir objeto de personalización completa
      const personalizacion: PersonalizacionCompleta = {
        portada: portadaConfig,
        nombrePersonalizado: nombrePersonalizado.trim() || undefined,
        tamano: tamanoInfo
          ? {
              id: tamanoInfo.id,
              nombre: tamanoInfo.nombre,
              dimensiones: `${tamanoInfo.dimensiones.ancho}cm × ${tamanoInfo.dimensiones.alto}cm`,
              orientacion: tamanoInfo.orientacion,
            }
          : undefined,
        preview: previewImage || undefined,
      };

      // Guardar configuración en localStorage
      const configKey = `personalizacion_${producto._id}`;
      localStorage.setItem(configKey, JSON.stringify(personalizacion));

      // Agregar al carrito con precio dinámico según tamaño
      addToCart({
        productId: producto._id,
        title: `${producto.title}${
          tamanoInfo ? ` - ${tamanoInfo.nombre}` : ""
        }`,
        price: precioFinal,
        image: producto.image,
        isPersonalizable: true,
        personalizacion,
      });

      toast({
        title: "Agregado al carrito",
        description: `${producto.title} personalizado agregado exitosamente`,
      });

      // Navegar al carrito
      navigate("/cart");
    } catch (error) {
      console.error("Error al agregar al carrito:", error);
      toast({
        title: "Error",
        description: "No se pudo agregar el producto al carrito",
        variant: "destructive",
      });
    } finally {
      setAgregandoAlCarrito(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-brown-light">Cargando producto...</p>
        </div>
      </div>
    );
  }

  // No product state
  if (!producto) {
    return null;
  }

  const config = producto.configuracionPersonalizable;
  const personalizacion = config?.personalizacion;

  // Validar que el producto tenga configuración de personalización
  if (!personalizacion) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-brown-dark mb-2">
            Producto no personalizable
          </h2>
          <p className="text-brown-light mb-4">
            Este producto no tiene configuración de personalización disponible.
          </p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-primary/10 py-6 md:py-12">
      <div className="container mx-auto px-3 sm:px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-3 md:mb-4 -ml-2"
            size="sm"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>

          <h1 className="font-playfair text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-brown-dark mb-2">
            Personaliza tu {producto.title}
          </h1>
          <p className="text-sm md:text-base text-brown-light/80">
            Crea un producto único y especial con tus preferencias
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Columna izquierda: Configuración */}
          <div className="lg:col-span-2 space-y-3 md:space-y-4">
            {/* Selector de tamaño */}
            {personalizacion.opcionesTamano &&
              personalizacion.opcionesTamano.length > 0 && (
                <Card>
                  <CardHeader className="p-3 md:p-4 pb-2 md:pb-3">
                    <CardTitle className="font-playfair text-brown-dark flex items-center gap-2 text-sm md:text-base">
                      📏 Selecciona el Tamaño
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 md:p-4 pt-0">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                      {personalizacion.opcionesTamano.map((tamano) => (
                        <button
                          key={tamano.id}
                          onClick={() => setTamanoSeleccionado(tamano.id)}
                          className={`
                          relative p-3 md:p-4 rounded-xl border-2 transition-all duration-200 touch-manipulation
                          ${
                            tamanoSeleccionado === tamano.id
                              ? "border-primary bg-primary/10 shadow-md"
                              : "border-brown-light/30 hover:border-primary/50 bg-white active:scale-98"
                          }
                        `}
                        >
                          {tamanoSeleccionado === tamano.id && (
                            <div className="absolute top-2 right-2 bg-primary text-brown-dark rounded-full p-1">
                              <svg
                                className="w-4 h-4"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </div>
                          )}
                          <div className="space-y-2">
                            <div className="flex items-baseline justify-between">
                              <p className="font-bold text-brown-dark text-base md:text-lg">
                                {tamano.nombre}
                              </p>
                              <p className="text-xl md:text-2xl font-bold text-primary">
                                ${tamano.precio}
                              </p>
                            </div>
                            <div className="text-xs md:text-sm text-brown-light/70 space-y-1">
                              <p className="flex items-center gap-2">
                                <span className="font-medium">Tamaño:</span>
                                <span>
                                  {tamano.dimensiones.ancho}cm ×{" "}
                                  {tamano.dimensiones.alto}cm
                                </span>
                              </p>
                              <p className="flex items-center gap-2">
                                <span className="font-medium">
                                  Orientación:
                                </span>
                                <span className="capitalize">
                                  {tamano.orientacion}
                                </span>
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

            {/* Personalizador de portada */}
            {(personalizacion.permiteEstampados ||
              personalizacion.permiteImagenPersonalizada) && (
              <PersonalizadorPortada
                estampadosDisponibles={estampadosDisponibles}
                permiteImagenPersonalizada={
                  personalizacion.permiteImagenPersonalizada
                }
                onSelectionChange={setPortadaConfig}
                value={portadaConfig}
              />
            )}

            {/* Hojas Internas Incluidas - Se muestra automáticamente según categoría */}
            <VistaHojasInternasPorCategoria
              categoria={
                personalizacion.categoriaEstampados as
                  | "infantil"
                  | "literario"
                  | "general"
              }
              hojasIdsEspecificos={personalizacion.hojasInternasIds}
              orientacion={orientacionPreview}
            />

            {/* Campo de nombre personalizado */}
            {personalizacion.campoNombre?.activo && (
              <Card>
                <CardContent className="p-3 md:p-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="nombre-personalizado"
                      className="text-sm md:text-base font-playfair text-brown-dark flex items-center gap-2"
                    >
                      ✍️ {personalizacion.campoNombre?.label}
                    </Label>
                    <Input
                      id="nombre-personalizado"
                      placeholder={personalizacion.campoNombre?.placeholder}
                      value={nombrePersonalizado}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value.length <=
                          personalizacion.campoNombre?.maxCaracteres || 100
                        ) {
                          setNombrePersonalizado(value);
                        }
                      }}
                      maxLength={personalizacion.campoNombre?.maxCaracteres || 100}
                      className="text-sm md:text-base"
                    />
                    <p className="text-xs text-brown-light/60">
                      {nombrePersonalizado.length} /{" "}
                      {personalizacion.campoNombre?.maxCaracteres || 100} caracteres
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Columna derecha: Preview y resumen */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-4 space-y-3">
              {/* Preview Card */}
              <Card className="border-2 border-primary/30">
                <CardHeader className="bg-gradient-to-br from-primary/10 to-primary/5 p-3 md:p-4">
                  <CardTitle className="font-playfair text-brown-dark text-sm md:text-base">
                    Vista Previa
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 md:p-4" ref={previewRef}>
                  <div className="space-y-4">
                    {/* Preview de portada con sistema de capas */}
                    <PreviewCapas
                      portadaConfig={portadaConfig}
                      cubiertaUrl={obtenerUrlCubierta()}
                      nombrePersonalizado={nombrePersonalizado}
                      orientacion={orientacionPreview}
                      estampadosDisponibles={estampadosDisponibles}
                      onClick={() => {
                        if (
                          portadaConfig.estampado ||
                          portadaConfig.imagenPersonalizada
                        ) {
                          setPreviewModalOpen(true);
                        }
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Resumen de compra */}
              <Card className="border-2 border-primary/30">
                <CardContent className="p-3 md:p-4 space-y-2 md:space-y-3">
                  <div className="flex justify-between items-center text-sm md:text-base">
                    <span className="text-brown-light">Producto</span>
                    <span className="font-medium text-brown-dark text-right">
                      {producto.title}
                    </span>
                  </div>

                  {/* Mostrar tamaño seleccionado */}
                  {personalizacion.opcionesTamano && tamanoSeleccionado && (
                    <div className="bg-primary/5 rounded-lg p-2 space-y-0.5">
                      <p className="text-xs font-medium text-brown-dark">
                        Tamaño: {
                          personalizacion.opcionesTamano.find(
                            (t) => t.id === tamanoSeleccionado
                          )?.nombre
                        }
                      </p>
                      <p className="text-xs text-brown-light/70">
                        {
                          personalizacion.opcionesTamano.find(
                            (t) => t.id === tamanoSeleccionado
                          )?.dimensiones.ancho
                        }
                        cm ×{" "}
                        {
                          personalizacion.opcionesTamano.find(
                            (t) => t.id === tamanoSeleccionado
                          )?.dimensiones.alto
                        }
                        cm
                      </p>
                    </div>
                  )}

                  <div className="flex justify-between items-center text-sm md:text-base">
                    <span className="text-brown-light">Precio base</span>
                    <span className="text-brown-light/60 line-through">
                      ${producto.price}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-brown-light/20">
                    <span className="font-medium text-brown-dark text-sm md:text-base">
                      Precio personalizado
                    </span>
                    <span className="text-xl md:text-2xl font-bold text-primary">
                      ${precioFinal}
                    </span>
                  </div>

                  <Button
                    onClick={handleAddToCart}
                    disabled={agregandoAlCarrito}
                    className="w-full bg-primary hover:bg-primary/90 text-brown-dark font-medium touch-manipulation h-11 md:h-12"
                    size="lg"
                  >
                    {agregandoAlCarrito ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Agregando...
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-5 w-5 mr-2" />
                        Agregar al Carrito
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-brown-light/60">
                    Podrás revisar tu pedido antes de finalizar la compra
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Modal para vista previa ampliada */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-2xl md:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-brown-dark text-lg md:text-xl">
              Vista Previa Detallada
            </DialogTitle>
            <p className="text-sm text-brown-light/70 mt-2">
              Así se verá tu {producto.title} personalizado
            </p>
          </DialogHeader>
          <div className="mt-3 md:mt-4">
            <div className="relative bg-white rounded-lg p-4 md:p-6 border-2 border-brown-light/20">
              <PreviewCapas
                portadaConfig={portadaConfig}
                cubiertaUrl={obtenerUrlCubierta()}
                nombrePersonalizado={nombrePersonalizado}
                orientacion={orientacionPreview}
                estampadosDisponibles={estampadosDisponibles}
                className="mx-auto max-w-md"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
